/**
 * Diagnostics card for the reconciliation integrity sentinel (15-minute
 * job): last run time, rows tracked, transitions detected, and a table of
 * integrity_events (newest first) -- each one an out-of-app write that
 * cleared is_reconciled while a matcher pointer stayed live, or cleared a
 * pointer inconsistent with build_release_update semantics. Detection only;
 * this panel never heals anything, it only surfaces what the sentinel found.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Play, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { reconciliationSentinelAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function relativeTime(isoDate) {
  if (!isoDate) return 'Never';
  const diff = Date.now() - new Date(isoDate).getTime();
  const hours = diff / 3600000;
  if (hours < 1) return Math.round(hours * 60) + 'm ago';
  if (hours < 24) return Math.round(hours) + 'h ago';
  return Math.round(hours / 24) + 'd ago';
}

export function ReconciliationSentinelPanel() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [runningNow, setRunningNow] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await reconciliationSentinelAPI.diagnostics();
      setStatus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const runNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await reconciliationSentinelAPI.runNow();
      if (data?.transitions_detected) {
        toast.warning(`Sentinel run complete: ${data.transitions_detected} integrity event(s) detected`);
      } else {
        toast.success(`Sentinel run complete: ${data?.rows_tracked ?? 0} row(s) tracked, no issues found`);
      }
      await fetchStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunningNow(false);
    }
  };

  const lastRun = status?.last_run || null;
  const events = status?.events || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">Reconciliation Integrity Sentinel</CardTitle>
            <CardDescription>
              Every 15 minutes: flags an out-of-app write that clears is_reconciled while a
              matcher pointer stays live. Detection only, never heals.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runNow}
              disabled={runningNow}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              data-testid="diag-reconciliation-sentinel-run-now-btn"
            >
              {runningNow ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Run Now
            </button>
            <button
              onClick={fetchStatus}
              disabled={loading}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Load'}
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground flex-wrap">
          <span>Last run: {relativeTime(lastRun?.run_at)}</span>
          <Badge variant="outline" className="text-[10px]">{lastRun?.rows_tracked ?? 0} rows tracked</Badge>
          <Badge
            className={
              (lastRun?.transitions_detected ?? 0) > 0
                ? 'bg-red-100 text-red-700 text-[10px]'
                : 'bg-green-100 text-green-700 text-[10px]'
            }
          >
            {lastRun?.transitions_detected ?? 0} transition(s) last run
          </Badge>
          <Badge variant="outline" className="text-[10px]">{status?.total_events ?? 0} total integrity_events</Badge>
        </div>

        {events.length === 0 ? (
          <p className="text-xs text-muted-foreground">No integrity events recorded.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Detected</TableHead>
                <TableHead>Transaction</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Previous is_reconciled</TableHead>
                <TableHead>Current is_reconciled</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs">{e.detected_at}</TableCell>
                  <TableCell className="text-xs font-mono">{e.transaction_id}</TableCell>
                  <TableCell className="text-xs">
                    <span className="inline-flex items-center gap-1 text-amber-700">
                      <AlertTriangle className="h-3 w-3" />
                      {e.kind}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {e.window_start || '(first run)'} &rarr; {e.window_end}
                  </TableCell>
                  <TableCell className="text-xs">{String(e.previous_state?.is_reconciled)}</TableCell>
                  <TableCell className="text-xs">{String(e.current_state?.is_reconciled)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
